/**
 * HVILKE MODULER HAR ORGANISASJONEN? (Rettigheter 30.09; valgt i Control Room fra 0483.)
 *
 * `useHouseModules(me)` er svaret menyen, rutene og Rettigheter deler: de fem modulene fra
 * `/v1/me` (`memberships[].modules`). `orgModulesOf` oversetter dem til seksjonene på Rettigheter
 * (`PERMISSION_SECTIONS`): Generelt, Økonomi og Deling har alle; Booking følger Booking, Artister
 * følger Byrå, Festival følger Festival eller Spillested (begge er arrangementer), Spillested og
 * Konserter følger sine egne, og Spillesteder (salene andre hus har lagt ut) følger sin (0922).
 *
 * OVERGANGEN: mangler `modules` (et API bygget før 0483), utledes de som backfillen gjorde, av
 * typen, festivallista og `/concerts/presence` — de samme spørringene menyen alltid har gjort, så
 * det koster ingen ekstra forespørsel. Med feltet til stede spørres ingenting.
 */
import type {
  MeResponse,
  OrganizationKind,
  OrganizationModule,
  PermissionModule,
} from '@tvitvi/shared';
import { useQuery } from '@tanstack/react-query';

import { festivalsQueryOptions } from '@/api/queries/festivals';
import { useMe } from '@/api/queries/me';
import { actorFromMe, allows } from '@/auth/actor';
import { declaredModulesOf, deriveModules } from '@/auth/organization-modules';
import { organizationKindOf } from '@/auth/organization-kind';
import { concertPresenceQueryOptions } from '@/features/venue/concert-presence';

export interface OrgModuleFacts {
  kind: OrganizationKind;
  /** Modulene som er på. */
  modules: ReadonlySet<OrganizationModule>;
}

/** Den rene regelen for Rettigheter, testet uten nettleser. */
export function orgModulesOf(facts: OrgModuleFacts): ReadonlySet<PermissionModule> {
  const sections = new Set<PermissionModule>(['general', 'money', 'sharing']);
  const { modules } = facts;
  if (modules.has('booking')) sections.add('booking');
  if (modules.has('agency')) sections.add('artists');
  if (modules.has('festival') || modules.has('venue')) sections.add('festival');
  /* Mannskap og vakter (sak 231.5): festival og spillested har begge mannskap. */
  if (modules.has('festival') || modules.has('venue')) sections.add('crew');
  if (modules.has('venue')) sections.add('venue');
  if (modules.has('concerts')) sections.add('concerts');
  /* Spillesteder er en modul siden 0922 (253.59), ikke typen. */
  if (modules.has('venues')) sections.add('venues');
  return sections;
}

export interface HouseModules {
  modules: ReadonlySet<OrganizationModule>;
  /** Svaret er klart. Alltid sant når `/v1/me` har modulene; i overgangen når spørringene har svart. */
  ready: boolean;
}

export function useHouseModules(me: MeResponse): HouseModules {
  const declared = declaredModulesOf(me);
  const actor = actorFromMe(me);
  const kind = organizationKindOf(me);
  const organizationId = me.activeOrganizationId;
  const legacy = declared === undefined;

  const festivalsEnabled =
    legacy && organizationId !== null && kind === 'festival' && allows(actor, 'festival:list');
  const festivals = useQuery({
    ...festivalsQueryOptions(organizationId ?? ''),
    enabled: festivalsEnabled,
  });
  const presenceEnabled =
    legacy && organizationId !== null && kind !== 'personal' && allows(actor, 'concert:list');
  const presence = useQuery({
    ...concertPresenceQueryOptions(organizationId ?? ''),
    enabled: presenceEnabled,
  });

  if (!legacy) return { modules: new Set(declared), ready: true };

  const items = festivals.data?.items ?? [];
  return {
    modules: deriveModules({
      kind,
      hasFestival: items.some((item) => item.kind === 'festival'),
      hasVenue: items.some((item) => item.kind === 'venue'),
      hasConcerts: presence.data?.hasConcerts === true || presence.data?.concertsModule === true,
    }),
    ready:
      (!festivalsEnabled || festivals.isSuccess || festivals.isError) &&
      (!presenceEnabled || presence.isSuccess || presence.isError),
  };
}

export interface OrgModules {
  modules: ReadonlySet<PermissionModule>;
  /** Alle kildene har svart (eller er ikke aktuelle). Før det tegner siden skjelettet. */
  ready: boolean;
}

export function useOrgModules(): OrgModules {
  const me = useMe();
  const house = useHouseModules(me);
  return {
    modules: orgModulesOf({ kind: organizationKindOf(me), modules: house.modules }),
    ready: house.ready,
  };
}
